import { motion } from 'motion/react'
import { Loader2Icon } from 'lucide-react'
import { type PendingItem, useCanvasStore } from '../canvas-store'
import { useDragInteraction } from '../use-drag-interaction'
import { SNAP_CURVE } from '../motion-tokens'

type Props = { item: PendingItem }

// Optimistic placeholder rendered immediately after a drop / paste / file-pick.
// Position is mutable here — when the upload finishes, the real item is created at
// whatever position the user dragged the placeholder to.
export function PendingCard({ item }: Props) {
  const override = useCanvasStore((s) => s.dragOverrides[item.id])
  const isDragging = !!override
  const position = override?.position ?? item.position
  const updatePending = useCanvasStore((s) => s.updatePending)

  const drag = useDragInteraction({
    itemId: item.id,
    basePosition: item.position,
    onCommit: (next) => updatePending(item.id, { position: next }),
  })

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.24, ease: SNAP_CURVE }}
      className="absolute"
      style={{
        left: position.x,
        top: position.y,
        width: item.size.w,
        height: item.size.h,
      }}>
      <div
        {...drag}
        className={[
          'bg-card ring-border/60 relative h-full w-full overflow-hidden rounded-lg shadow-sm ring-1',
          isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
        ].join(' ')}>
        {item.previewUrl && (item.kind === 'image' || item.kind === 'pdf') ? (
          <img
            src={item.previewUrl}
            alt=""
            draggable={false}
            className="h-full w-full object-cover"
          />
        ) : item.previewUrl && item.kind === 'video' ? (
          <video
            src={item.previewUrl}
            className="pointer-events-none h-full w-full object-cover"
            muted
            playsInline
          />
        ) : (
          <div className="text-muted-foreground flex h-full w-full items-center justify-center text-xs">
            {item.label ?? 'Adding…'}
          </div>
        )}
        <div className="bg-background/40 pointer-events-none absolute inset-0 flex items-center justify-center backdrop-blur-[2px]">
          <Loader2Icon className="text-foreground/70 animate-spin" size={20} />
        </div>
      </div>
    </motion.div>
  )
}
